import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import PMBBadge from 'components/PMBBadge'
import PMBTotalFooter from 'components/PMBTotalFooter'
import { capitalizeEveryWord } from 'helpers'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { ChevronLeft, X } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  Container,
  FormGroup,
  Input,
  Label,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_EMPLOYEE_PAYMENTS } from 'utils/pathUrl'
import CardUploadImage from './CardUploadImage'
import { useSubmitDisbursementBatchMutation } from './employeePaymentsListApiSlice'
import {
  clearDisbursements,
  setBatchName,
  updateDisbursementProofImg,
} from './employeePaymentsListSlice'

const PaymentSummary = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()

  const { disbursementData } = useSelector(
    (state) => state.employeePaymentsList,
  )

  const [submitDisbursementBatch, { isLoading }] =
    useSubmitDisbursementBatchMutation()

  const [editingImageIndex, setEditingImageIndex] = useState(null)

  const filterUsers = disbursementData?.disburses
    ?.map((user, originalIndex) => ({
      ...user,
      originalIndex,
    }))
    .filter((e) => e?.amount !== 0)

  const totalAmount = disbursementData?.disburses?.reduce((acc, item) => {
    return acc + item.amount
  }, 0)

  const handleBack = () => {
    navigate(-1)
  }

  const handleSubmit = async () => {
    try {
      const cashPaymentsWithoutPhoto = filterUsers?.filter(
        (user) => user.disburseMethod === 'CASH' && !user.proofImgs,
      )

      if (cashPaymentsWithoutPhoto?.length > 0) {
        enqueueSnackbar('Please upload photo proof for all cash payments', {
          variant: 'warning',
        })
        return
      }

      const payload = {
        batchName: disbursementData?.batchName,
        disburses: filterUsers?.map((i) => ({
          employeeUserId: i.employeeUserId,
          amount: i.amount,
          disburseMethod: i.disburseMethod,
          proofImgs: i.proofImgs || '',
          notes: i.notes || '',
          processreportIds: i.processreportIds || [],
          createdAt: i.createdAt
            ? (typeof i.createdAt === 'string' ? i.createdAt : new Date(i.createdAt).toISOString())
            : new Date().toISOString(),
        })),
      }

      const response = await submitDisbursementBatch(payload).unwrap()

      if (response?.id) {
        enqueueSnackbar('Payment processed successfully', {
          variant: 'success',
        })
        dispatch(clearDisbursements())
        navigate(URL_EMPLOYEE_PAYMENTS)
      }
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || error?.message || 'Failed to submit payment',
        {
          variant: 'error',
        },
      )
    }
  }

  const handleRemoveImage = (originalIndex) => {
    dispatch(
      updateDisbursementProofImg({ index: originalIndex, proofImgs: '' }),
    )
    setEditingImageIndex(null)
  }

  const toggleEditImage = (originalIndex) => {
    if (editingImageIndex === originalIndex) {
      setEditingImageIndex(null)
    } else {
      setEditingImageIndex(originalIndex)
    }
  }

  return (
    <>
      <Container fluid className="custom-container">
        <div className="mt-3">
          <BreadCrumbs
            breadCrumbParent2="List of Disbursements"
            breadCrumbParent2Link={URL_EMPLOYEE_PAYMENTS}
            breadCrumbActive="Salary Payment Summary"
          />
        </div>

        <div className="d-flex align-items-start pb-4">
          <ChevronLeft
            size={18}
            className="me-2 mt-1"
            onClick={handleBack}
            style={{ cursor: 'pointer' }}
          />
          <div>
            <P size="medium" className="fw-bold m-0">
              Confirm Disbursement
            </P>

            <P size="small" className="text-muted m-0">
              Review payment details before processing
            </P>
          </div>
        </div>

        <Card className="border-0 shadow-none">
          <CardBody>
            <div className="">
              {/* SUMMARY */}
              <P size="medium" className="mb-4">
                Salary Payment Summary
              </P>

              {/* INPUT BATCH NAME */}
              <div className="mb-4">
                <FormGroup>
                  <Label className="text-sm pb-1">
                    Batch ID <span style={{ color: '#b42218' }}>*</span>
                  </Label>
                  <Input
                    placeholder="Input batch id"
                    name="batchname"
                    id="batchname"
                    onChange={(e) => {
                      dispatch(setBatchName(e.target.value))
                    }}
                    value={disbursementData?.batchName || ''}
                  />
                </FormGroup>
              </div>

              <div className="d-flex flex-column gap-3">
                {filterUsers?.map((user) => {
                  const { originalIndex } = user

                  return (
                    <Card key={user.employeeUserId} className="border">
                      <CardBody className="p-3">
                        {/* HEADER SECTION */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold">
                                {user.employeeName}
                              </span>
                              <PMBBadge
                                variant={
                                  user?.disburseMethod === 'BANK_TRANSFER'
                                    ? 'warning'
                                    : user?.disburseMethod === 'CASH'
                                      ? 'verify'
                                      : 'danger'
                                }
                                title={capitalizeEveryWord(
                                  user?.disburseMethod,
                                )}
                                className="px-2 w-auto"
                                size="small"
                              />
                            </div>

                            <div className="text-muted small mb-2">
                              {user.rolesName}
                            </div>
                          </div>

                          <div className="text-end ms-3">
                            <span className="fw-bold text-primary">
                              Rp{newFormatThousandSeparator(user.amount)}
                            </span>
                          </div>
                        </div>

                        {user.notes && (
                          <div className="text-muted small mb-3">
                            <span className="fst-italic">Note:</span>{' '}
                            {user.notes}
                          </div>
                        )}

                        {user.disburseMethod === 'CASH' && (
                          <div>
                            {user.proofImgs &&
                            editingImageIndex !== originalIndex ? (
                              <div className="d-flex align-items-center gap-2">
                                <div style={{ position: 'relative' }}>
                                  <img
                                    style={{
                                      width: '100px',
                                      height: '100px',
                                      objectFit: 'cover',
                                    }}
                                    className="img-fluid rounded"
                                    src={user.proofImgs}
                                    alt="PROOF_IMAGE"
                                    loading="lazy"
                                  />
                                  <Button
                                    size="sm"
                                    color="danger"
                                    style={{
                                      position: 'absolute',
                                      top: '-8px',
                                      right: '-8px',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                    onClick={() =>
                                      handleRemoveImage(originalIndex)
                                    }
                                  >
                                    <X size={14} />
                                  </Button>
                                </div>
                                <Label
                                  onClick={() => toggleEditImage(originalIndex)}
                                  className="text-sm text-primary mb-0"
                                  style={{ cursor: 'pointer' }}
                                >
                                  Change
                                </Label>
                              </div>
                            ) : (
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <span className="text-danger fw-semibold small">
                                    Photo proof is required
                                  </span>
                                </div>
                                <CardUploadImage
                                  key={`upload-${user.employeeUserId}-${originalIndex}-${editingImageIndex}`}
                                  type={`PAYMENT_PROOF_${originalIndex}`}
                                  employeeUserId={user.employeeUserId}
                                  disbursementIndex={originalIndex}
                                  className="border w-100"
                                />
                                {editingImageIndex === originalIndex && (
                                  <Label
                                    onClick={() => setEditingImageIndex(null)}
                                    className="text-sm text-muted mt-2"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    Cancel
                                  </Label>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  )
                })}
              </div>

              {/* Empty State */}
              {(!filterUsers || filterUsers?.length === 0) && (
                <div className="text-center py-5">
                  <P className="text-muted">No disbursements to process</P>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
        <div style={{ height: isMobile ? '120px' : '80px' }}></div>
      </Container>

      {filterUsers?.length > 0 && (
        <PMBTotalFooter
          total={totalAmount}
          onSubmit={handleSubmit}
          noMinimum
          testId="emp-pay-submit-button"
          customTotalText={
            <div>
              <div className="d-flex gap-2">
                Total Amount:
                <P bold className="text-primary">
                  {' '}
                  Rp{newFormatThousandSeparator(totalAmount)}
                </P>
              </div>
            </div>
          }
          textBtn="Confirm & Process"
          isDisable={!disbursementData?.batchName || isLoading}
        />
      )}
    </>
  )
}

export default PaymentSummary
